import { useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import { useAppSelector } from '@store/hooks'
import {
  getAllAdjustmentTypeSettings,
  addAdjustmentTypeSetting,
  updateAdjustmentTypeSetting,
  removeAdjustmentTypeSetting,
} from '@store/actions/adjustmentType.action'
import {
  EditingAdjustmentEntry,
  DisplayAdjustmentType,
} from '../billingSettings.types'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { GlobalLoadingActions } from '@context/GlobalLoadingContext'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'

interface UseAdjustmentTypesReturn {
  adjustmentTypes: DisplayAdjustmentType[]
  editingEntries: Map<string, EditingAdjustmentEntry>
  newEntry: EditingAdjustmentEntry | null
  handleAddNew: () => void
  handleChange: (
    id: string,
    field: 'status' | 'name' | 'modifier' | 'category',
    value: string,
  ) => void
  handleSave: (id: string) => Promise<void>
  handleEdit: (id: string) => void
  handleCancel: (id: string) => void
  handleDelete: (id: string) => Promise<void>
  handleToggleStatus: (id: string, checked: boolean) => Promise<void>
  getFilteredData: (
    searchQuery: string,
    filters: Record<string, string[]>,
  ) => DisplayAdjustmentType[]
  reset: () => void
}

export const useAdjustmentTypes = (
  showToast?: (notification: NotificationToastData) => void,
  showLoading?: GlobalLoadingActions['showLoading'],
  hideLoading?: GlobalLoadingActions['hideLoading'],
  getListFilters?: () => Parameters<typeof getAllAdjustmentTypeSettings>[0],
): UseAdjustmentTypesReturn => {
  const dispatch = useDispatch<AppDispatch>()
  const { adjustmentTypes } = useSelector(
    (state: RootState) => state.adjustmentType,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicId = useMemo(
    () => selectedClinic?.clinic_id || localStorage.getItem('clinic_id'),
    [selectedClinic?.clinic_id],
  )

  const [editingEntries, setEditingEntries] = useState<
    Map<string, EditingAdjustmentEntry>
  >(new Map())
  const [newEntry, setNewEntry] = useState<EditingAdjustmentEntry | null>(null)

  const handleAddNew = useCallback((): void => {
    setNewEntry({
      adjustment_type_id: '',
      status: SettingStatus.Enabled,
      name: '',
      modifier: 'Positive',
      category: 'Adjustment',
    })
  }, [])

  const handleChange = useCallback(
    (
      id: string,
      field: 'status' | 'name' | 'modifier' | 'category',
      value: string,
    ): void => {
      if (id === '') {
        setNewEntry((prev) =>
          prev
            ? {
                ...prev,
                [field]: value,
              }
            : null,
        )
      } else {
        setEditingEntries((prev) => {
          const updated = new Map(prev)
          const current = updated.get(id) || {
            adjustment_type_id: id,
            status:
              adjustmentTypes.find((a) => a.adjustment_type_id === id)
                ?.status || SettingStatus.Enabled,
            name:
              adjustmentTypes.find((a) => a.adjustment_type_id === id)?.name ||
              '',
            modifier:
              adjustmentTypes.find((a) => a.adjustment_type_id === id)
                ?.modifier || 'Positive',
            category:
              adjustmentTypes.find((a) => a.adjustment_type_id === id)
                ?.category || 'Adjustment',
          }
          updated.set(id, {
            ...current,
            [field]: value,
          })
          return updated
        })
      }
    },
    [adjustmentTypes],
  )

  const handleSave = useCallback(
    async (id: string): Promise<void> => {
      try {
        showLoading?.({ container: '.billing-settings-modal' })
        if (id === '') {
          if (!newEntry) return
          if (!clinicId) return
          await dispatch(
            addAdjustmentTypeSetting({
              status: newEntry.status,
              name: newEntry.name,
              modifier: newEntry.modifier,
              category: newEntry.category,
              clinic_id: clinicId,
            }),
          ).unwrap()
          setNewEntry(null)
          showToast?.(
            createSuccessToast(
              'Adjustment type saved successfully',
              'Adjustment type has been saved successfully.',
            ),
          )
        } else {
          const editingEntry = editingEntries.get(id)
          if (!editingEntry) return

          const existingEntry = adjustmentTypes.find(
            (a) => a.adjustment_type_id === id,
          )
          if (!existingEntry) {
            if (showToast) {
              showToast(
                createFailedToast(
                  'Adjustment type not found',
                  'Cannot update: entry not found in backend data.',
                ),
              )
            }
            setEditingEntries((prev) => {
              const updated = new Map(prev)
              updated.delete(id)
              return updated
            })
            return
          }

          await dispatch(
            updateAdjustmentTypeSetting({
              adjustment_type_id: existingEntry.adjustment_type_id,
              status: editingEntry.status,
              name: editingEntry.name,
              modifier: editingEntry.modifier,
              category: editingEntry.category,
              clinic_id: existingEntry.clinic_id,
            }),
          ).unwrap()
          setEditingEntries((prev) => {
            const updated = new Map(prev)
            updated.delete(id)
            return updated
          })
          showToast?.(
            createSuccessToast(
              'Adjustment type updated successfully',
              'Adjustment type has been updated successfully.',
            ),
          )
        }

        await dispatch(getAllAdjustmentTypeSettings(getListFilters?.()))
      } catch (error) {
        showToast?.(
          createFailedToast(
            'Adjustment type not saved',
            'Adjustment type not saved. Please try again.',
          ),
        )
      } finally {
        hideLoading?.('.billing-settings-modal')
      }
    },
    [
      dispatch,
      newEntry,
      editingEntries,
      adjustmentTypes,
      clinicId,
      getListFilters,
      showToast,
      showLoading,
      hideLoading,
    ],
  )

  const handleEdit = useCallback(
    (id: string): void => {
      const entry = adjustmentTypes.find((a) => a.adjustment_type_id === id)
      if (entry) {
        setEditingEntries((prev) => {
          const updated = new Map(prev)
          updated.set(id, {
            adjustment_type_id: entry.adjustment_type_id,
            status: entry.status,
            name: entry.name,
            modifier: entry.modifier,
            category: entry.category,
          })
          return updated
        })
      }
    },
    [adjustmentTypes],
  )

  const handleCancel = useCallback((id: string): void => {
    if (id === '') {
      setNewEntry(null)
    } else {
      setEditingEntries((prev) => {
        const updated = new Map(prev)
        updated.delete(id)
        return updated
      })
    }
  }, [])

  const handleDelete = useCallback(
    async (id: string): Promise<void> => {
      try {
        showLoading?.({ container: '.billing-settings-modal' })
        await dispatch(removeAdjustmentTypeSetting(id)).unwrap()
        await dispatch(getAllAdjustmentTypeSettings(getListFilters?.()))
        showToast?.(
          createSuccessToast(
            'Adjustment type deleted successfully',
            'Adjustment type has been deleted successfully.',
          ),
        )
      } catch (error) {
        showToast?.(
          createFailedToast(
            'Adjustment type not deleted',
            'Adjustment type not deleted. Please try again.',
          ),
        )
      } finally {
        hideLoading?.('.billing-settings-modal')
      }
    },
    [dispatch, getListFilters, showToast, showLoading, hideLoading],
  )

  const handleToggleStatus = useCallback(
    async (id: string, checked: boolean): Promise<void> => {
      try {
        showLoading?.({ container: '.billing-settings-modal' })
        const existingEntry = adjustmentTypes.find(
          (a) => a.adjustment_type_id === id,
        )
        if (!existingEntry) {
          if (showToast) {
            showToast(
              createFailedToast(
                'Adjustment type not found',
                'Cannot update: entry not found in backend data.',
              ),
            )
          }
          return
        }

        const newStatus = checked
          ? SettingStatus.Enabled
          : SettingStatus.Disabled
        await dispatch(
          updateAdjustmentTypeSetting({
            adjustment_type_id: existingEntry.adjustment_type_id,
            status: newStatus,
            name: existingEntry.name,
            modifier: existingEntry.modifier,
            category: existingEntry.category,
            clinic_id: existingEntry.clinic_id,
          }),
        ).unwrap()
        await dispatch(getAllAdjustmentTypeSettings(getListFilters?.()))
        showToast?.(
          createSuccessToast(
            'Adjustment type status updated successfully',
            'Adjustment type status has been updated successfully.',
          ),
        )
      } catch (error) {
        showToast?.(
          createFailedToast(
            'Adjustment type status not updated',
            'Adjustment type status not updated. Please try again.',
          ),
        )
      } finally {
        hideLoading?.('.billing-settings-modal')
      }
    },
    [
      dispatch,
      adjustmentTypes,
      getListFilters,
      showToast,
      showLoading,
      hideLoading,
    ],
  )

  const getFilteredData = useCallback(
    (
      searchQuery: string,
      filters: Record<string, string[]>,
    ): DisplayAdjustmentType[] => {
      let filtered = [...adjustmentTypes]

      // Apply search filter
      if (searchQuery) {
        filtered = filtered.filter((item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      }

      // Apply category filter
      if (filters.category && filters.category.length > 0) {
        filtered = filtered.filter((item) =>
          filters.category.includes(item.category),
        )
      }

      // Apply modifier filter
      if (filters.modifier && filters.modifier.length > 0) {
        filtered = filtered.filter((item) =>
          filters.modifier.includes(item.modifier),
        )
      }

      // Apply status filter
      if (filters.status && filters.status.length > 0) {
        filtered = filtered.filter((item) =>
          filters.status.includes(item.status),
        )
      }

      const result: DisplayAdjustmentType[] = []

      // Add new entry if it exists
      if (newEntry && clinicId) {
        result.push({
          adjustment_type_id: '',
          status: newEntry.status,
          name: newEntry.name,
          modifier: newEntry.modifier,
          category: newEntry.category,
          clinic_id: clinicId,
          isEditing: true,
        })
      }

      // Add all adjustment types with editing state
      filtered.forEach((entry) => {
        const editingEntry = editingEntries.get(entry.adjustment_type_id)
        result.push({
          ...entry,
          ...(editingEntry && {
            status: editingEntry.status,
            name: editingEntry.name,
            modifier: editingEntry.modifier,
            category: editingEntry.category,
          }),
          isEditing: editingEntries.has(entry.adjustment_type_id),
        })
      })

      return result
    },
    [adjustmentTypes, newEntry, editingEntries, clinicId],
  )

  const reset = useCallback((): void => {
    setEditingEntries(new Map())
    setNewEntry(null)
  }, [])

  // Memoize the return object to prevent unnecessary re-renders
  // Note: adjustmentTypes is not directly used - components use getFilteredData() instead
  return useMemo(
    () => ({
      adjustmentTypes: [] as DisplayAdjustmentType[],
      editingEntries,
      newEntry,
      handleAddNew,
      handleChange,
      handleSave,
      handleEdit,
      handleCancel,
      handleDelete,
      handleToggleStatus,
      getFilteredData,
      reset,
    }),
    [
      editingEntries,
      newEntry,
      handleAddNew,
      handleChange,
      handleSave,
      handleEdit,
      handleCancel,
      handleDelete,
      handleToggleStatus,
      getFilteredData,
      reset,
    ],
  )
}
